"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.formatDate = formatDate;
var _getFormattedDate = require("../internals/getFormattedDate");
var _mapDateFormatToDatePattern = require("../internals/mapDateFormatToDatePattern");
/**
 * Javascript Date timezones behavior:
 *  - If the input date doesn't contain timezone e.g.: new Date('December 4, 2020 20:34:25'), then it is considered to be in the runtime timezone and the formatted string is in the same timezone.
 *  - If the input date contains timezone e.g.: new Date('December 4, 2020 20:34:25 GMT+05:00'), then the formatted string is displayed in the runtime timezone.
 *  - If the input date is initialized as unix epoch (seconds since 1970) e.g.: new Date('1643311226'), then the formatted string is displayed in the runtime timezone because unix epoch is always in UTC.
 */

/**
 * Returns parameter `value` formatted according to selected date `format`.
 *
 * The timezone information in the date value is optional. If the timezone information is missing, then it is expected to be the tenant timezone.
 *
 * The `value` is formatted in the runtime culture in case:
 * - the {@link culture} setting is not provided,
 * - the calendar formatting rules or date patterns are not available in the {@link culture} setting,
 * - the custom pattern is not defined.
 *
 * @param date Date value to format
 * @param format The type of output string format (e.g. full date time)
 * @param culture Culture settings containing formatting rules
 * @param timeZone Timezone identifications
 * @param options Additional formatting options
 * @returns `value` formatted according to selected date `format`
 * @example
  // basic usage
  import { formatDate, useCulture } from '@ori/culture';
  const { culture, timeZone } = useCulture();
  const formattedDate = formatDate(new Date(), DateFormat.ShortDateTime, culture, timeZone);

  // use a custom pattern
  formatDate(new Date(), DateFormat.Custom, culture, timeZone, { customPattern: 'dd.MM.yyyy' });

  // enable transformation to the runtime (local) timezone
  formatDate(new Date(), DateFormat.ShortDateTime, culture, timeZone, { toLocalTimezone: true });
 */
// eslint-disable-next-line max-params -- needed for formatting
function formatDate(date, format, culture, timeZone, options) {
  const {
    customPattern,
    toLocalTimezone
  } = options ?? {};
  const {
    calendarFormat
  } = culture ?? {};
  const {
    patterns
  } = calendarFormat ?? {};
  const datePattern = (0, _mapDateFormatToDatePattern.mapDateFormatToDatePattern)(format, patterns, customPattern);
  if (!calendarFormat || !datePattern) {
    // it is preferred by the business to display the value formatted in the runtime culture than no value at all
    return date.toLocaleString();
  }

  // keep the given value in the runtime (OS and browser) timezone
  if (toLocalTimezone) {
    return (0, _getFormattedDate.getFormattedDate)(date, datePattern, calendarFormat);
  }
  return (0, _getFormattedDate.getFormattedDate)(
  // convert the given value to the tenant time zome
  new Date(date.toLocaleString('en-US', {
    timeZone: timeZone?.tzdbId
  })), datePattern, calendarFormat);
}
//# sourceMappingURL=formatDate.js.map